import type { PlatformConfig } from './types';

/**
 * The one place the engine learns what machine it is on.
 *
 * A module-level registry rather than a context object threaded through every
 * call, because that choice is what makes the extraction affordable:
 * `enqueue(input)`, `drain()`, `pullAll()` and every repository function keep
 * the signatures they already had. Threading would have touched every call
 * site in both apps to gain an injection point neither of them needs — there
 * is exactly one platform per process, decided at boot and never swapped.
 *
 * The exception is tests, which do swap it. `resetPlatform()` exists for them.
 */
let current: PlatformConfig | null = null;

/** Install the adapters. Call once, at boot, before anything touches the db. */
export function configurePlatform(config: PlatformConfig): void {
  current = config;
}

/**
 * The installed adapters.
 *
 * Throws rather than returning null: every caller would immediately
 * dereference it, so a null check at each one buys nothing but noise, and the
 * failure it would be hiding — the engine running before boot finished — needs
 * to be loud. The message names the fix because the person seeing it is
 * usually wiring up a new platform for the first time.
 */
export function platform(): PlatformConfig {
  if (!current) {
    throw new Error(
      'Platform adapters are not installed. Call configurePlatform({ db, media, net, lifecycle, ids, secureStore, api }) at app startup before using the sync engine.',
    );
  }
  return current;
}

/** Whether adapters have been installed. For boot code, not for call sites. */
export function isPlatformConfigured(): boolean {
  return current !== null;
}

/** Drop the installed adapters. Intended for test teardown. */
export function resetPlatform(): void {
  current = null;
}
